/*
 * Pure rules behind the Key Facts tab: which nav filter a key fact falls
 * under, how the content groups by section, what a row's status looks like,
 * and how live events patch the cached case.
 */
import type {
  AskKind,
  FactBadge,
  FactRowItem,
  KeyFact,
  KeyFactActionBody,
  KeyFactAlternative,
  KeyFactEditor,
  KeyFactPending,
  KeyFactsCase,
  KeyFactsFilter,
  KeyFactSource,
  KeyFactsSummary,
  NoticeBriefData,
  NoticeBriefPayload,
  QueueCursor,
  ReviewChoice
} from "./types";

/** The key fact kinds a USCIS notice produces. */
export const NOTICE_KINDS: ReadonlySet<string | undefined> = new Set([
  "notice",
  "notice_issue",
  "notice_strategy"
]);

export const NOTICE_WORKFLOWS: ReadonlySet<string | undefined> = new Set([
  "rfe",
  "noir_noid"
]);

export const NOTICE_LABEL: Record<string, string> = {
  rfe: "RFE",
  noid: "NOID",
  noir: "NOIR"
};

/** The short label of the notice read on the case ("NOID"), or null. */
export const noticeLabelOf = (facts: readonly KeyFact[]): string | null => {
  const notice = facts.find((f) => f.kind === "notice");
  const value = notice?.value;
  const kind =
    value && typeof value === "object" && "notice_type" in value
      ? value.notice_type
      : null;
  return typeof kind === "string" ? NOTICE_LABEL[kind] ?? null : null;
};

export const FILTERS: ReadonlyArray<{ id: KeyFactsFilter; label: string }> = [
  { id: "all", label: "All" },
  { id: "proposed", label: "Proposed" },
  { id: "changed", label: "Changed" },
  { id: "needs_input", label: "Needs Input" }
];

/** Proposed and awaiting the attorney's review. */
export const awaitsReview = (fact: KeyFact): boolean =>
  fact.review && fact.attorney_state === "proposed" && fact.status === "fresh";

const issueInForce = (fact: KeyFact): boolean =>
  (fact.value as { in_force?: boolean } | null)?.in_force !== false;

/**
 * What the case's in-force notice issues say, lowercased: a base-visa key
 * fact the notice never touches is background on a notice case.
 */
export const noticeText = (facts: readonly KeyFact[]): string =>
  facts
    .filter((f) => f.kind === "notice_issue" && issueInForce(f))
    .map((f) => {
      const v = (f.value ?? {}) as Record<string, unknown>;
      return [
        v.issue,
        v.title,
        v.criterion_or_ground,
        v.uscis_reasoning,
        ...(Array.isArray(v.evidence_requested) ? v.evidence_requested : [])
      ]
        .filter((x): x is string => typeof x === "string")
        .join(" ");
    })
    .join(" ")
    .replace(/_/g, " ")
    .toLowerCase();

/**
 * On a notice case, a key fact outside the notice that waits for input and
 * that no issue mentions: USCIS didn't ask about it. Quiet facts stay in
 * their section but leave the filters and the badge.
 */
export const isQuiet = (
  fact: KeyFact,
  workflow: string | undefined,
  text: string
): boolean => {
  if (!NOTICE_WORKFLOWS.has(workflow) || NOTICE_KINDS.has(fact.kind))
    return false;
  if (fact.status !== "needs_input") return false;
  const names = [fact.kind.replace(/_/g, " "), fact.label.toLowerCase()];
  return !names.some((n) => n && text.includes(n));
};

export const matchesFilter = (
  fact: KeyFact,
  filter: KeyFactsFilter
): boolean => {
  switch (filter) {
    case "proposed":
      return awaitsReview(fact);
    case "changed":
      return fact.status === "stale";
    case "needs_input":
      return fact.status === "needs_input";
    default:
      return fact.status !== "disabled";
  }
};

/** The facts the filters and the badge count: quiet ones left out. */
export const countedFacts = (
  facts: readonly KeyFact[],
  workflow?: string
): KeyFact[] => {
  if (!NOTICE_WORKFLOWS.has(workflow)) return [...facts];
  const text = noticeText(facts);
  return facts.filter((f) => !isQuiet(f, workflow, text));
};

export const filterCounts = (
  facts: readonly KeyFact[],
  workflow?: string
): Record<KeyFactsFilter, number> => {
  const out: Record<KeyFactsFilter, number> = {
    all: 0,
    proposed: 0,
    changed: 0,
    needs_input: 0
  };
  for (const fact of countedFacts(facts, workflow)) {
    for (const { id } of FILTERS) if (matchesFilter(fact, id)) out[id] += 1;
  }
  return out;
};

/**
 * What needs the attorney: the facts the Proposed, Changed and Needs Input
 * filters list, so the badge and the filters agree.
 */
export const attentionCount = (
  facts: readonly KeyFact[],
  workflow?: string
): number =>
  countedFacts(facts, workflow).filter((f) =>
    FILTERS.some(({ id }) => id !== "all" && matchesFilter(f, id))
  ).length;

/** The sections the case has key facts in, in catalog order. */
export const presentSections = (data: KeyFactsCase): string[] => {
  const used = new Set(data.facts.map((f) => f.section).filter(Boolean));
  const ordered = data.sections.filter((s) => used.has(s));
  const rest = [...used].filter((s) => !data.sections.includes(s)).sort();
  return [...ordered, ...rest];
};

/** `[[section, facts]]` in section order, empty sections left out. */
export const groupBySection = (
  facts: readonly KeyFact[],
  sections: readonly string[]
): Array<[string, KeyFact[]]> => {
  const groups = new Map<string, KeyFact[]>();
  for (const fact of facts) {
    const section = fact.section || "Other";
    groups.set(section, [...(groups.get(section) ?? []), fact]);
  }
  const order = [
    ...sections,
    ...[...groups.keys()].filter((s) => !sections.includes(s))
  ];
  return order
    .filter((s) => groups.has(s))
    .map((s) => [s, groups.get(s) ?? []]);
};

/** Kinds with one instance per exhibit ("Venue Rankings: <exhibit>"), shown
 * as one row per kind once there are several. */
const GROUPED_KINDS = new Set(["venue_rankings", "high_remuneration"]);

/** "Venue Rankings: CV.pdf" -> "CV.pdf": an instance's name under its kind. */
export const instanceLabel = (fact: KeyFact): string => {
  const at = fact.label.indexOf(": ");
  return at === -1 ? fact.label : fact.label.slice(at + 2);
};

const kindLabel = (fact: KeyFact): string => {
  const at = fact.label.indexOf(": ");
  return at === -1 ? fact.kind : fact.label.slice(0, at);
};

/** A section's facts as rows, the instances of a grouped kind under one. */
export const groupInstances = (facts: readonly KeyFact[]): FactRowItem[] => {
  const byKind = new Map<string, KeyFact[]>();
  for (const fact of facts)
    if (GROUPED_KINDS.has(fact.kind))
      byKind.set(fact.kind, [...(byKind.get(fact.kind) ?? []), fact]);
  const out: FactRowItem[] = [];
  const placed = new Set<string>();
  for (const fact of facts) {
    const instances = byKind.get(fact.kind);
    if (!instances || instances.length < 2) {
      out.push({ kind: "fact", fact });
    } else if (!placed.has(fact.kind)) {
      placed.add(fact.kind);
      out.push({
        kind: "instances",
        key: fact.kind,
        label: kindLabel(fact),
        facts: instances
      });
    }
  }
  return out;
};

export type FactTone =
  | "attention"
  | "proposed"
  | "settled"
  | "checking"
  | "off";

/** How a row's value control looks: its border and icon. */
export const factTone = (fact: KeyFact): FactTone => {
  if (fact.status === "disabled") return "off";
  if (fact.status === "checking") return "checking";
  if (
    fact.status === "stale" ||
    fact.status === "needs_input" ||
    fact.status === "failed" ||
    fact.flags.some((f) => f.severity === "must")
  )
    return "attention";
  if (awaitsReview(fact)) return "proposed";
  return "settled";
};

/** "26 · 3 need attention · 2 to review": a grouped row's summary. */
export const instanceSummary = (facts: readonly KeyFact[]): string => {
  const attention = facts.filter((f) => factTone(f) === "attention").length;
  const review = facts.filter(awaitsReview).length;
  const checking = facts.filter((f) => f.status === "checking").length;
  return [
    `${facts.length}`,
    attention ? `${attention} need attention` : "",
    review ? `${review} to review` : "",
    checking ? `${checking} checking` : ""
  ]
    .filter(Boolean)
    .join(" · ");
};

/** The most pressing tone among a group's instances. */
export const groupTone = (facts: readonly KeyFact[]): FactTone => {
  const tones = facts.map(factTone);
  for (const tone of ["attention", "checking", "proposed", "settled"] as const)
    if (tones.includes(tone)) return tone;
  return "off";
};

/** The recomputed value is nothing: the record no longer gives one. Older
 * records carry no status, only the "Missing" display. */
export const pendingIsMissing = (pending: KeyFactPending): boolean =>
  pending.status
    ? pending.status === "needs_input"
    : pending.display === "Missing";

/** One line for the status, under the value. */
export const statusText = (fact: KeyFact): string => {
  if (fact.locked) return "As filed";
  switch (fact.status) {
    case "checking":
      return "Checking…";
    case "stale":
      if (!fact.pending) return "The record changed";
      return pendingIsMissing(fact.pending)
        ? "The record no longer supports this value"
        : `The record now gives ${fact.pending.display}`;
    case "needs_input":
      return "Needs input";
    case "failed":
      return fact.error || "Couldn't compute";
    case "disabled":
      return "Off for this case";
    default:
      break;
  }
  if (fact.attorney_state === "confirmed") return "Confirmed";
  if (fact.attorney_state === "overridden") return "Set by you";
  return fact.review ? "Proposed" : "From the record";
};

const LONG_VALUE = 60;

/** Long values sit under the name instead of in the control. */
export const isLongValue = (fact: KeyFact): boolean =>
  (fact.display || "").length > LONG_VALUE;

/** Keys an alternative carries about itself, not part of the value. */
const ALTERNATIVE_META = new Set(["reason", "matched_on", "score", "why"]);

export const alternativeText = (alt: KeyFactAlternative): string => {
  const head = alt.display || alt.title || alt.label;
  const code = (alt.soc_code ?? alt.code) as string | undefined;
  if (head && code && !String(head).includes(String(code)))
    return `${code} ${head}`;
  if (head) return String(head);
  const scalar = Object.entries(alt).find(
    ([k, v]) => !ALTERNATIVE_META.has(k) && typeof v === "string"
  );
  return scalar ? String(scalar[1]) : "Alternative";
};

/** The value to set when the attorney picks `alt`. */
export const alternativeValue = (
  alt: KeyFactAlternative
): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(alt).filter(([k]) => !ALTERNATIVE_META.has(k))
  );

/** "NOIR_Notice" at page 1 -> "NOIR Notice, p. 1": a source as a reader names it. */
export const sourceLabel = (source: KeyFactSource): string => {
  const name = (source.title || source.dataset || source.url || source.kind)
    .replace(/_/g, " ")
    .trim();
  return source.page ? `${name}, p. ${source.page}` : name;
};

const MAX_SOURCES_SHOWN = 2;

/** "Offer Letter, O*NET +2": where the value came from, in a line. */
export const sourcesText = (fact: KeyFact): string => {
  const used = fact.sources.filter(
    (s) => !fact.excluded_sources.includes(s.id)
  );
  const names = used.slice(0, MAX_SOURCES_SHOWN).map(sourceLabel);
  const more = used.length - names.length;
  return names.join(", ") + (more > 0 ? ` +${more}` : "");
};

/** What needs the attorney, one fact at a time: the Proposed, Changed and
 * Needs Input facts in section order, so the queue and the badge agree. */
export const reviewQueue = (
  facts: readonly KeyFact[],
  workflow: string | undefined,
  sections: readonly string[]
): KeyFact[] =>
  groupBySection(
    countedFacts(facts, workflow).filter((f) =>
      FILTERS.some(({ id }) => id !== "all" && matchesFilter(f, id))
    ),
    sections
  ).flatMap(([, items]) => items);

/** The fact on the review card: the cursor's, or whichever took its place. */
export const cursorIndex = (
  queue: readonly KeyFact[],
  cursor: QueueCursor
): number => {
  const at = queue.findIndex((f) => f.key === cursor.key);
  return at !== -1 ? at : Math.max(0, Math.min(cursor.index, queue.length - 1));
};

/** The review card's question about `fact`. */
export const reviewPrompt = (
  fact: KeyFact
): { title: string; description?: string } => {
  if (fact.status === "stale" && fact.pending)
    return {
      title: pendingIsMissing(fact.pending)
        ? "The record no longer supports this value"
        : `${fact.label} changed in the record`,
      description: fact.pending.rationale || undefined
    };
  if (fact.status === "needs_input")
    return {
      title: `${fact.label} needs input`,
      description: fact.rationale || undefined
    };
  return { title: fact.label, description: fact.rationale || undefined };
};

const MAX_ALTERNATIVES = 6;

/**
 * The answers to a fact's review question, the recommended one first: the
 * proposed value, or for a changed value the recomputed one (keeping the
 * current value when the record no longer gives one), then the alternatives.
 * A fact that needs input, or a settled one being changed, offers only the
 * alternatives; "Other" covers the rest.
 */
export const reviewChoices = (fact: KeyFact): ReviewChoice[] => {
  const alternatives = fact.alternatives
    .slice(0, MAX_ALTERNATIVES)
    .map((alt, i): ReviewChoice => {
      const text = alternativeText(alt);
      return {
        value: `alt:${i}`,
        label: text,
        description: alt.reason ? String(alt.reason) : undefined,
        body: {
          action: "override",
          value: alternativeValue(alt),
          display: text,
          reason: "Chose an alternative"
        }
      };
    });
  if (fact.status === "stale" && fact.pending) {
    const keep: ReviewChoice = {
      value: "keep",
      label: fact.display || "Keep the current value",
      description:
        fact.attorney_state === "overridden"
          ? "Keep the value you set"
          : "Keep the confirmed value",
      body: { action: "dismiss" }
    };
    if (pendingIsMissing(fact.pending))
      return [
        { ...keep, recommended: true },
        {
          value: "clear",
          label: "Clear the value",
          description: "The record no longer gives one",
          body: { action: "confirm" }
        },
        ...alternatives
      ];
    const from = (fact.pending.sources ?? []).map(sourceLabel).join(", ");
    return [
      {
        value: "recomputed",
        label: fact.pending.display,
        description: from
          ? `Recomputed from ${from}`
          : "Recomputed from the record",
        recommended: true,
        body: { action: "confirm" }
      },
      keep,
      ...alternatives
    ];
  }
  if (awaitsReview(fact))
    return [
      {
        value: "proposed",
        label: fact.display || statusText(fact),
        description: sourcesText(fact) || undefined,
        recommended: true,
        body: { action: "confirm" }
      },
      ...alternatives
    ];
  return alternatives;
};

/** Parts of a value that point elsewhere rather than say something. */
const HIDDEN_PARTS = new Set(["node_id", "citation", "kind", "url"]);
/** Bookkeeping a value carries for the backend, never shown. */
const INTERNAL_PART =
  /(^|_)(key|node_id|id|fingerprint)$|^(in_force|procedural|page|order|issue|ready)$/;

const ACRONYMS: Record<string, string> = {
  uscis: "USCIS",
  soc: "SOC",
  onet: "O*NET",
  url: "URL",
  i: "I",
  noid: "NOID",
  noir: "NOIR",
  rfe: "RFE",
  lca: "LCA"
};

const humanize = (key: string): string =>
  key
    .split("_")
    .filter(Boolean)
    .map(
      (word) => ACRONYMS[word] ?? word.charAt(0).toUpperCase() + word.slice(1)
    )
    .join(" ");

const partText = (part: unknown): string => {
  if (part === null || part === undefined || part === "") return "";
  if (Array.isArray(part)) return part.map(partText).filter(Boolean).join(", ");
  if (typeof part === "boolean") return part ? "Yes" : "No";
  if (typeof part !== "object") return String(part);
  const entries = part as Record<string, unknown>;
  if (entries.display) return String(entries.display);
  if (entries.title) return String(entries.title);
  return Object.entries(entries)
    .filter(
      ([k, v]) => !HIDDEN_PARTS.has(k) && v !== null && typeof v !== "object"
    )
    .map(([k, v]) => `${humanize(k)}: ${partText(v)}`)
    .join(", ");
};

const partAt = (value: Record<string, unknown>, path: string): unknown =>
  path
    .split(".")
    .reduce<unknown>(
      (at, name) =>
        at && typeof at === "object"
          ? (at as Record<string, unknown>)[name]
          : undefined,
      value
    );

/** `[["Title", "Associate Professor"], ...]`: a structured value's parts,
 * the spec's `paths` first in their order, then the rest; bookkeeping hidden. */
export const valueRows = (
  value: unknown,
  paths?: string[]
): Array<[string, string]> => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return [];
  const entries = value as Record<string, unknown>;
  const lead = (paths ?? []).map((path): [string, unknown] => [
    path.split(".").pop() ?? path,
    partAt(entries, path)
  ]);
  const tops = new Set((paths ?? []).map((path) => path.split(".")[0]));
  const rest = Object.entries(entries).filter(
    ([k]) => !tops.has(k) && !HIDDEN_PARTS.has(k) && !INTERNAL_PART.test(k)
  );
  return [...lead, ...rest]
    .map(([k, v]): [string, string] => [humanize(k), partText(v)])
    .filter(([, text]) => text);
};

/** One editable part of a structured value, at a dotted path into it. */
export type ValuePart = {
  path: string;
  label: string;
  numeric?: boolean;
  placeholder?: string;
};

/** The parts the attorney edits on a structured key fact; the backend
 * recomputes what follows from them (annual pay, the O*NET entry). */
export const VALUE_PARTS: Record<string, ValuePart[]> = {
  offered_role: [
    { path: "title", label: "Job title" },
    { path: "worksite.city", label: "Worksite city" },
    { path: "worksite.state", label: "Worksite state" },
    { path: "salary.amount", label: "Salary", numeric: true },
    {
      path: "salary.unit",
      label: "Paid per",
      placeholder: "year, month, week or hour"
    }
  ],
  occupation: [{ path: "soc_code", label: "SOC code", placeholder: "29-1131" }]
};

export const valueAt = (value: unknown, path: string): string => {
  let at: unknown = value;
  for (const name of path.split(".")) {
    if (!at || typeof at !== "object") return "";
    at = (at as Record<string, unknown>)[name];
  }
  return at === null || at === undefined ? "" : String(at);
};

/** The edited parts as a nested value (`{salary: {amount: 40}}`), only those
 * that differ from `current`; null when nothing changed. */
export const editedParts = (
  parts: readonly ValuePart[],
  current: unknown,
  entries: Record<string, string>
): Record<string, unknown> | null => {
  const out: Record<string, unknown> = {};
  const changed = parts.filter(
    (part) => (entries[part.path] ?? "").trim() !== valueAt(current, part.path)
  );
  for (const part of changed) {
    const text = (entries[part.path] ?? "").trim();
    const names = part.path.split(".");
    let at = out;
    for (const name of names.slice(0, -1))
      at = (at[name] ??= {}) as Record<string, unknown>;
    const parsed =
      part.numeric && text ? Number(text.replace(/[$,]/g, "")) : text;
    at[names[names.length - 1]] = part.numeric && text === "" ? null : parsed;
  }
  return changed.length ? out : null;
};

// ---------------------------------------------------------------------------
// The Insights rows: one status, the headline value, and what to ask.
// ---------------------------------------------------------------------------

/** A row's one status. */
export const statusBadge = (fact: KeyFact): FactBadge => {
  if (fact.locked) return { label: "As Filed", tone: "neutral" };
  switch (fact.status) {
    case "disabled":
      return { label: "Off", tone: "neutral" };
    case "checking":
      return { label: "Checking", tone: "neutral" };
    case "stale":
      return { label: "Out of Date", tone: "warn" };
    case "needs_input":
      return { label: "Needs You", tone: "warn" };
    case "failed":
      return { label: "Couldn't Compute", tone: "danger" };
    default:
      break;
  }
  if (awaitsReview(fact)) return { label: "Proposed", tone: "info" };
  if (fact.attorney_state === "confirmed")
    return { label: "Confirmed", tone: "success" };
  if (fact.attorney_state === "overridden")
    return { label: "Set by You", tone: "accent" };
  return { label: "From the Record", tone: "neutral" };
};

/** Done with: confirmed, set by the attorney, or settled from the record. */
export const isSettled = (fact: KeyFact): boolean =>
  !fact.locked &&
  fact.status === "fresh" &&
  !awaitsReview(fact) &&
  fact.attorney_state !== "proposed";

/** The current value in the fallback `choice` editor. */
export const CURRENT_CHOICE = "current";

/** The "Other" choice's value. */
export const OTHER_CHOICE = "other";

/** The fact's editor: the backend's, else the alternatives, the parts or a
 * text box, for an older backend. */
export const editorOf = (fact: KeyFact): KeyFactEditor => {
  if (fact.editor) return fact.editor;
  if (fact.alternatives.length) {
    const current = fact.display
      ? [{ value: CURRENT_CHOICE, label: fact.display }]
      : [];
    return {
      type: "choice",
      options: [
        ...current,
        ...fact.alternatives.slice(0, MAX_ALTERNATIVES).map((alt, i) => ({
          value: `alt:${i}`,
          label: alternativeText(alt),
          hint: alt.reason ? String(alt.reason) : undefined
        }))
      ],
      selected: current.length ? [CURRENT_CHOICE] : [],
      other: { label: "Other" }
    };
  }
  const parts = VALUE_PARTS[fact.kind];
  if (parts)
    return {
      type: "parts",
      parts: parts.map((p) => ({
        path: p.path,
        label: p.label,
        kind: p.numeric ? "number" : "text",
        placeholder: p.placeholder
      }))
    };
  return { type: "text" };
};

const names = (items: unknown, key: string): string[] =>
  Array.isArray(items)
    ? items
        .map((item) =>
          item && typeof item === "object"
            ? (item as Record<string, unknown>)[key]
            : item
        )
        .filter((name): name is string => typeof name === "string" && !!name)
    : [];

/** A list value as chips (criteria, technologies, sectors, CIP programs);
 * null for a value that reads as one line. */
export const valueChips = (fact: KeyFact): string[] | null => {
  if (fact.status === "needs_input" || fact.status === "disabled") return null;
  const { editor } = fact;
  if (editor?.type === "multi") {
    const labels = new Map(
      (editor.options ?? []).map((o) => [o.value, o.label])
    );
    const chips = (editor.selected ?? []).map((v) => labels.get(v) ?? v);
    return chips.length ? chips : null;
  }
  const value = (fact.value ?? {}) as Record<string, unknown>;
  const chips =
    fact.kind === "criteria_to_argue"
      ? names(value.selected, "name")
      : fact.kind === "cet"
      ? names(value.areas, "name")
      : fact.kind === "cisa"
      ? names(value.sectors, "name")
      : fact.kind === "stem" && value.designated
      ? (Array.isArray(value.matches) ? value.matches : [])
          .map((m: Record<string, unknown>) =>
            [m.cip_code ?? m.cip_series, m.title].filter(Boolean).join(" ")
          )
          .filter(Boolean)
      : [];
  return chips.length ? chips : null;
};

/** The first `must` flag: one warn line under the value. */
export const headlineFlag = (fact: KeyFact): string | null =>
  fact.flags.find((f) => f.severity === "must")?.message ?? null;

const hasOptions = (editor: KeyFactEditor): boolean =>
  (editor.type === "multi" || editor.type === "choice") &&
  (editor.options?.length ?? 0) > 0;

/** What the row asks, or null when it asks nothing. */
export const askKind = (
  fact: KeyFact,
  editor: KeyFactEditor = editorOf(fact)
): AskKind | null => {
  if (fact.locked) return null;
  if (fact.status === "stale") return fact.pending ? "stale" : "proposed";
  if (fact.status === "needs_input")
    return hasOptions(editor)
      ? "pick"
      : fact.missing.length
      ? "missing"
      : "enter";
  if (awaitsReview(fact))
    return editor.type === "multi" && hasOptions(editor) ? "pick" : "proposed";
  return null;
};

/** "Passport biographic page" and where the case looked for it. */
export const missingText = (
  fact: KeyFact
): { what: string; lookedIn: string } => {
  const what = fact.missing
    .map((m) => String(m.input ?? m.label ?? ""))
    .filter(Boolean)
    .join("; ");
  const looked = fact.missing.flatMap((m) => {
    const at = m.looked ?? m.looked_in;
    return Array.isArray(at) ? at.map(String) : at ? [String(at)] : [];
  });
  return {
    what: what || "A document",
    lookedIn: [...new Set(looked)].join(", ")
  };
};

/** The question a `pick` or `enter` ask puts. */
export const askQuestion = (
  fact: KeyFact,
  editor: KeyFactEditor = editorOf(fact)
): string => {
  if (editor.type === "multi")
    return `Which ${(editor.noun?.[1] ?? fact.label).toLowerCase()} apply?`;
  if (editor.type === "choice") return `Which ${fact.label} is right?`;
  if (fact.status === "needs_input") return `What is the ${fact.label}?`;
  return reviewPrompt(fact).title;
};

/** The case chat's prompt for Request From Client. */
export const requestPrompt = (fact: KeyFact): string =>
  `Ask the client for what ${fact.label} still needs: ${
    missingText(fact).what
  }. Draft the client request.`;

const sameSet = (a: readonly string[], b: readonly string[]): boolean =>
  a.length === b.length && a.every((v) => b.includes(v));

/** A checklist's answer: confirm the proposal unchanged, else the list;
 * null when nothing changed on a settled fact. */
export const multiBody = (
  fact: KeyFact,
  editor: KeyFactEditor,
  picked: readonly string[]
): KeyFactActionBody | null => {
  if (!picked.length) return null;
  if (sameSet(picked, editor.selected ?? []) && fact.status === "fresh")
    return awaitsReview(fact) ? { action: "confirm" } : null;
  return { action: "override", value: [...picked] };
};

/** A pick-one answer: an option's value, an alternative of an older backend,
 * or the typed "Other". */
export const choiceBody = (
  fact: KeyFact,
  editor: KeyFactEditor,
  picked: string,
  other: string
): KeyFactActionBody | null => {
  if (picked === OTHER_CHOICE) {
    const text = other.trim();
    return text ? { action: "override", value: text } : null;
  }
  if ((editor.selected ?? []).includes(picked))
    return awaitsReview(fact) || fact.status === "stale"
      ? { action: "confirm" }
      : null;
  const alt = picked.startsWith("alt:")
    ? fact.alternatives[Number(picked.slice(4))]
    : undefined;
  if (!fact.editor && alt)
    return {
      action: "override",
      value: alternativeValue(alt),
      display: alternativeText(alt)
    };
  return { action: "override", value: picked };
};

/** A form's answer: the changed parts, or confirming an untouched proposal. */
export const partsBody = (
  fact: KeyFact,
  editor: KeyFactEditor,
  entries: Record<string, string>
): KeyFactActionBody | null => {
  const parts: ValuePart[] = (editor.parts ?? []).map((p) => ({
    path: p.path,
    label: p.label,
    numeric: p.kind === "number"
  }));
  const value = editedParts(parts, fact.value, entries);
  if (value) return { action: "override", value };
  return awaitsReview(fact) ? { action: "confirm" } : null;
};

/** A typed or dated answer. */
export const textBody = (
  fact: KeyFact,
  text: string
): KeyFactActionBody | null => {
  const value = text.trim();
  if (!value) return null;
  if (value === fact.display && fact.status === "fresh")
    return awaitsReview(fact) ? { action: "confirm" } : null;
  return { action: "override", value };
};

/** Counts for the strip: what needs the attorney, what is proposed, what
 * is done. */
export const insightCounts = (
  facts: readonly KeyFact[],
  workflow?: string
): { need: number; proposed: number; settled: number } => {
  const counted = countedFacts(facts, workflow);
  return {
    need: counted.filter(
      (f) => f.status === "needs_input" || f.status === "stale"
    ).length,
    proposed: counted.filter(awaitsReview).length,
    settled: counted.filter(isSettled).length
  };
};

// ---------------------------------------------------------------------------
// Live updates over `/api/events`.
// ---------------------------------------------------------------------------

export const upsertFact = (data: KeyFactsCase, fact: KeyFact): KeyFactsCase => {
  const at = data.facts.findIndex((f) => f.key === fact.key);
  const facts =
    at === -1
      ? [...data.facts, fact]
      : data.facts.map((f, i) => (i === at ? fact : f));
  return { ...data, facts };
};

export const removeFact = (data: KeyFactsCase, key: string): KeyFactsCase => ({
  ...data,
  facts: data.facts.filter((f) => f.key !== key)
});

export type KeyFactsStateEvent = {
  individual_id: string;
  reconcile_state?: string | null;
  reconcile_finished_at?: string | null;
  summary?: KeyFactsSummary | null;
};

export const applyState = (
  data: KeyFactsCase,
  event: KeyFactsStateEvent
): KeyFactsCase => ({
  ...data,
  settings: {
    ...data.settings,
    reconcile_state: event.reconcile_state ?? data.settings.reconcile_state,
    reconcile_finished_at:
      event.reconcile_finished_at ?? data.settings.reconcile_finished_at
  },
  summary: event.summary ?? data.summary
});

/** "3 of 4 done": how far a notice's issue analyses have got. */
export const noticeProgress = (
  facts: KeyFact[],
  noticeKey: string
): { done: number; total: number; running: number; failed: number } => {
  const issues = facts.filter((f) => {
    const value = f.value as Record<string, unknown> | null;
    return (
      f.kind === "notice_issue" &&
      f.has_analysis &&
      value?.notice_key === noticeKey
    );
  });
  return {
    done: issues.filter((f) => f.analysis_state === "current").length,
    running: issues.filter(
      (f) => f.analysis_state === "running" || f.analysis_state === "queued"
    ).length,
    failed: issues.filter((f) => f.analysis_state === "failed").length,
    total: issues.length
  };
};

/**
 * The explorer badge on a notice case: "5 Issues · 3 Need Evidence", warn
 * while an issue still needs evidence; null before the notice is read.
 */
export const noticeBadge = (
  brief: NoticeBriefData | null | undefined
): { text: string; label: string; tone: "warn" | "neutral" } | null => {
  const issues = brief?.issues ?? [];
  if (!issues.length) return null;
  const need = issues.filter((i) => i.our_position === "needs_evidence").length;
  const counted = `${issues.length} Issue${issues.length === 1 ? "" : "s"}`;
  return {
    text: need ? `${counted} · ${need} Need Evidence` : counted,
    label: `${brief?.label ?? "Notice"}: ${issues.length} issue${
      issues.length === 1 ? "" : "s"
    }${need ? `, ${need} still need${need === 1 ? "s" : ""} evidence` : ""}`,
    tone: need ? "warn" : "neutral"
  };
};

/**
 * The brief as it reads once `keys` are confirmed, before the refetch lands,
 * so the button and the "not confirmed yet" labels clear with the toast.
 */
export const withConfirmedIssues = (
  payload: NoticeBriefPayload | undefined,
  keys: readonly string[]
): NoticeBriefPayload | undefined => {
  const brief = payload?.brief;
  if (!payload || !brief || !keys.length) return payload;
  const confirmed = new Set(keys);
  const issues = brief.issues.map((issue) =>
    confirmed.has(issue.key) ? { ...issue, confirmed: true } : issue
  );
  return {
    ...payload,
    brief: {
      ...brief,
      issues,
      confirmed: issues.length > 0 && issues.every((i) => i.confirmed)
    }
  };
};

/** "Due Dec 11, 2026 · 12 days left", and the tone the countdown wears. */
export const dueCountdown = (
  date: string | null | undefined,
  now: Date = new Date()
): {
  text: string;
  days: number | null;
  tone: "danger" | "warn" | "neutral";
} => {
  if (!date)
    return { text: "No response deadline read", days: null, tone: "warn" };
  const due = new Date(`${date}T00:00:00`);
  if (Number.isNaN(due.getTime()))
    return { text: `Due ${date}`, days: null, tone: "neutral" };
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const days = Math.round((due.getTime() - today.getTime()) / 86_400_000);
  const day = due.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric"
  });
  const left =
    days < 0
      ? `${-days} day${days === -1 ? "" : "s"} overdue`
      : days === 0
      ? "due today"
      : `${days} day${days === 1 ? "" : "s"} left`;
  return {
    text: `Due ${day} · ${left}`,
    days,
    tone: days <= 14 ? "danger" : days <= 30 ? "warn" : "neutral"
  };
};
